import { useState, useCallback } from 'react';

interface ModalConfig {
  title?: string;
  content: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onClose?: () => void;
  preventClose?: boolean;
}

interface ModalInstance extends ModalConfig {
  id: string;
  isOpen: boolean;
}

let modalIdCounter = 0;

export const useModal = () => {
  const [modals, setModals] = useState<ModalInstance[]>([]);

  const showModal = useCallback((config: ModalConfig): string => {
    const id = `modal-${++modalIdCounter}`;
    const modal: ModalInstance = {
      ...config,
      id,
      isOpen: true
    };

    setModals((prev) => [...prev, modal]);
    return id;
  }, []);

  const closeModal = useCallback((id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  const closeAllModals = useCallback(() => {
    setModals([]);
  }, []);

  const updateModalPreventClose = useCallback((id: string, preventClose: boolean) => {
    setModals((prev) => prev.map((modal) => (modal.id === id ? { ...modal, preventClose } : modal)));
  }, []);

  return {
    showModal,
    closeModal,
    closeAllModals,
    updateModalPreventClose,
    modals
  };
};

// Global modal manager instance
let globalModalManager: ReturnType<typeof useModal> | null = null;

export const setGlobalModalManager = (manager: ReturnType<typeof useModal>) => {
  globalModalManager = manager;
};

export const getGlobalModalManager = () => {
  if (!globalModalManager) {
    throw new Error('Modal manager not initialized. Make sure to use ModalProvider.');
  }
  return globalModalManager;
};

// Convenience functions for global usage
export const showModal = (config: ModalConfig): string => {
  return getGlobalModalManager().showModal(config);
};

export const closeModal = (id: string) => {
  getGlobalModalManager().closeModal(id);
};

export const closeAllModals = () => {
  getGlobalModalManager().closeAllModals();
};

export const updateModalPreventClose = (id: string, preventClose: boolean) => {
  getGlobalModalManager().updateModalPreventClose(id, preventClose);
};
